import React from 'react';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';

/**
 * Shown while the frontend polls /api/checkout/status/{session_id} after returning
 * from Stripe. Three states: 'verifying' | 'success' (brief flash before reveal) | 'failed'.
 */
const PaymentVerifying = ({ state, message, onRetry }) => {
  const isVerifying = state === 'verifying';
  const isSuccess = state === 'success';
  const isFailed = state === 'failed';

  const Icon = isSuccess ? CheckCircle2 : isFailed ? XCircle : Loader2;
  const iconColor = isSuccess
    ? 'hsl(var(--gb-green-dark))'
    : isFailed
      ? 'hsl(var(--gb-bear-border))'
      : 'hsl(var(--gb-navy))';

  const title = isSuccess
    ? 'Payment confirmed'
    : isFailed
      ? 'Payment verification failed'
      : 'Verifying your payment…';

  const subtitle =
    message ||
    (isVerifying
      ? 'Stripe is confirming the transaction. This usually takes a few seconds.'
      : isSuccess
        ? 'Unlocking your full report.'
        : 'We could not confirm the payment with Stripe.');

  return (
    <section
      className="mt-8 rounded-lg border-2 px-6 py-8 flex flex-col items-center text-center"
      style={{
        borderColor: isFailed
          ? 'hsl(var(--gb-bear-border))'
          : 'hsl(var(--gb-gold))',
        backgroundColor: 'hsl(var(--gb-surface))',
      }}
      data-testid="payment-verifying"
      data-state={state}
    >
      <Icon
        size={44}
        strokeWidth={2}
        style={{ color: iconColor }}
        className={isVerifying ? 'animate-spin' : ''}
        aria-hidden="true"
      />
      <div
        className="mt-4 text-xl font-bold"
        style={{
          color: 'hsl(var(--gb-navy))',
          fontFamily: 'var(--font-display)',
        }}
        data-testid="payment-verifying-title"
      >
        {title}
      </div>
      <div
        className="mt-1 text-sm max-w-md"
        style={{ color: 'hsl(var(--gb-text-muted))' }}
        data-testid="payment-verifying-message"
      >
        {subtitle}
      </div>

      {isFailed && onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          data-testid="payment-verifying-retry"
          className="mt-5 inline-flex items-center gap-2 rounded-md px-4 py-2 font-medium text-sm transition-transform duration-150 hover:scale-[1.02] active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          style={{
            backgroundColor: 'hsl(var(--gb-navy))',
            color: 'hsl(var(--gb-primary-foreground))',
            ['--tw-ring-color']: 'hsl(var(--gb-gold))',
          }}
        >
          Check again
        </button>
      ) : null}
    </section>
  );
};

export default PaymentVerifying;
